import { QueryParameterBag } from '@smithy/types'

import {
  DroidMapMetadata,
  Paged,
  ServiceClient,
  ServiceClientContext,
  queryParameterBagFromOffsetPaginationControls,
  queryParameterBagFromPaginationControls,
} from '@droidmap/shared-service-contract'

import {
  AssetSetOperation,
  CreateAssetSetOperationInput,
  GetAssetSetOperationInput,
} from '../models/asset-set-operation.js'
import {
  Asset,
  AssetSet,
  AssetSetSummary,
  AssetWithPresignedUrl,
  CaptureTimeCorrection,
  CorrectAssetSetCaptureTimeInput,
  CreateAssetInput,
  CreateAssetSetInput,
  DeleteAssetInput,
  DeleteAssetSetInput,
  GetAssetByPathInput,
  GetAssetInput,
  GetAssetSetInput,
  GetAssetSetSummaryInput,
  GetAssetsInput,
  GetDownloadJobInput,
  GetDownloadJobResponse,
  GetProjectAssetSummaryInput,
  InitiateAssetSetDownloadInput,
  InitiateAssetSetDownloadResponse,
  ListAssetSetsByProjectInput,
  ListAssetSetsByProjectResult,
  ListAssetsByAssetSetInput,
  PresignedUrlType,
  ProjectAssetSummary,
  SlimAsset,
  UpdateAssetInput,
  UpdateAssetSetInput,
  WithMultipartPresignedUrls,
} from '../models/asset.js'
import {
  CreateImageAnnotationInput,
  DeleteImageAnnotationInput,
  ImageAnnotation,
  ListImageAnnotationsInput,
  ListNoObjectMarksInput,
  NoObjectMarks,
  SetNoObjectMarkInput,
  UpdateImageAnnotationInput,
} from '../models/image-annotation.js'
import {
  CreateShareLinkInput,
  CreateShareLinkResponse,
  GetShareDownloadInput,
  GetShareDownloadResponse,
  GetShareInfoInput,
  GetShareInfoResponse,
  InitiateShareDownloadInput,
  InitiateShareDownloadResponse,
  ListShareLinksInput,
  ListShareLinksResponse,
  RevokeShareLinkInput,
  RevokeShareLinkResponse,
} from '../models/share-link.js'

export class AssetServiceClient extends ServiceClient {
  public static readonly SERVICE_NAME = 'AssetService'

  constructor(input: ServiceClientContext) {
    super({ ...input, serviceName: AssetServiceClient.SERVICE_NAME })
  }

  /* -------------------------------- AssetSet -------------------------------- */

  createAssetSet = async (
    createAssetSetInput: CreateAssetSetInput,
    metadata?: DroidMapMetadata,
  ): Promise<AssetSet> => {
    const assetSet: AssetSet | undefined = await this.callApiRouteWithToken<
      CreateAssetSetInput,
      AssetSet
    >({
      method: 'POST',
      path: '/asset-sets',
      body: createAssetSetInput,
      metadata,
    })

    if (!assetSet) {
      throw new Error('Failed to create asset set')
    }

    return assetSet
  }

  getAssetSet = async (
    input: GetAssetSetInput,
    metadata?: DroidMapMetadata,
  ): Promise<AssetSet> => {
    let queryParameters: QueryParameterBag | undefined = undefined
    if (input.presignedUrlType) {
      queryParameters = {
        presignedUrlType: input.presignedUrlType,
      }
    }

    const assetSet = await this.callApiRouteWithToken<string, AssetSet>({
      method: 'GET',
      path: `/asset-sets/${input.assetSetId}`,
      queryParameters,
      metadata,
    })

    return assetSet
  }

  getAssetSetManifest = async (
    input: GetAssetSetInput,
    metadata?: DroidMapMetadata,
  ): Promise<Asset | AssetWithPresignedUrl | null> => {
    // First, get the asset set to check if it has a manifest path
    const assetSet = await this.getAssetSet(
      {
        assetSetId: input.assetSetId,
      },
      metadata,
    )

    if (!assetSet.manifestPath) {
      return null
    }

    // Use the manifest path to get the asset
    const asset = await this.getAssetByPath(
      {
        path: assetSet.manifestPath,
        assetSetId: input.assetSetId,
        presignedUrlType: input.presignedUrlType,
      },
      metadata,
    )

    return asset
  }

  /**
   * Correct a whole asset set's capture times (droidmap#1988). Stamps every
   * touched asset `manual`, which is what stops a later re-derivation
   * overwriting the operator's decision with an inference.
   */
  correctAssetSetCaptureTime = async (
    input: CorrectAssetSetCaptureTimeInput,
    metadata?: DroidMapMetadata,
  ): Promise<CaptureTimeCorrection> => {
    const { assetSetId, ...body } = input

    return this.callApiRouteWithToken<typeof body, CaptureTimeCorrection>({
      method: 'POST',
      path: `/asset-sets/${assetSetId}/capture-time`,
      body,
      metadata,
    })
  }

  updateAssetSet = async (
    updateAssetSetInput: UpdateAssetSetInput,
    metadata?: DroidMapMetadata,
  ): Promise<AssetSet> => {
    const { assetSetId } = updateAssetSetInput

    const asset = await this.callApiRouteWithToken<
      UpdateAssetSetInput,
      AssetSet
    >({
      method: 'PATCH',
      path: `/asset-sets/${assetSetId}`,
      body: updateAssetSetInput,
      metadata,
    })

    return asset
  }

  deleteAssetSet = async (
    input: DeleteAssetSetInput,
    metadata?: DroidMapMetadata,
  ): Promise<AssetSet> => {
    const assetSet = await this.callApiRouteWithToken<string, AssetSet>({
      method: 'DELETE',
      path: `/asset-sets/${input.assetSetId}`,
      metadata,
    })

    return assetSet
  }

  listAssetSetsByProject = async (
    input: ListAssetSetsByProjectInput,
    metadata?: DroidMapMetadata,
  ): Promise<ListAssetSetsByProjectResult> => {
    const {
      projectId,
      page,
      pageSize,
      filter,
      sortBy,
      sortDir,
      assetSetType,
      includeSummaries,
    } = input

    const queryParameters: QueryParameterBag = {
      ...queryParameterBagFromOffsetPaginationControls({
        page,
        pageSize,
        filter,
        sortBy,
        sortDir,
      }),
      ...(assetSetType ? { assetSetType } : {}),
      ...(includeSummaries ? { includeSummaries: 'true' } : {}),
    }

    return await this.callApiRouteWithToken<
      ListAssetSetsByProjectInput,
      ListAssetSetsByProjectResult
    >({
      method: 'GET',
      path: `/projects/${projectId}/asset-sets`,
      queryParameters,
      metadata,
    })
  }

  getAssetSetSummary = async (
    input: GetAssetSetSummaryInput,
    metadata?: DroidMapMetadata,
  ): Promise<AssetSetSummary> => {
    return await this.callApiRouteWithToken<string, AssetSetSummary>({
      method: 'GET',
      path: `/asset-sets/${input.assetSetId}/summary`,
      metadata,
    })
  }

  getProjectAssetSummary = async (
    input: GetProjectAssetSummaryInput,
    metadata?: DroidMapMetadata,
  ): Promise<ProjectAssetSummary> => {
    return await this.callApiRouteWithToken<string, ProjectAssetSummary>({
      method: 'GET',
      path: `/projects/${input.projectId}/asset-summary`,
      metadata,
    })
  }

  /* ---------------------------------- Asset --------------------------------- */

  createAsset = async (
    createAssetInput: CreateAssetInput,
    metadata?: DroidMapMetadata,
  ): Promise<AssetWithPresignedUrl> => {
    const queryParameters: QueryParameterBag = {
      presignedUrlType: PresignedUrlType.PUT,
    }

    const asset: AssetWithPresignedUrl | undefined =
      await this.callApiRouteWithToken<CreateAssetInput, AssetWithPresignedUrl>(
        {
          method: 'POST',
          path: `/assets`,
          body: createAssetInput,
          queryParameters,
          metadata,
        },
      )

    if (!asset) {
      throw new Error('Failed to create asset')
    }

    return asset
  }

  createAssetNoPresignedUrl = async (
    createAssetInput: CreateAssetInput,
    metadata?: DroidMapMetadata,
  ): Promise<Asset> => {
    const asset: Asset | undefined = await this.callApiRouteWithToken<
      CreateAssetInput,
      Asset
    >({
      method: 'POST',
      path: `/assets`,
      body: createAssetInput,
      metadata,
    })

    if (!asset) {
      throw new Error('Failed to create asset')
    }

    return asset
  }

  getAsset = async (
    input: GetAssetInput,
    metadata?: DroidMapMetadata,
  ): Promise<Asset | AssetWithPresignedUrl> => {
    let queryParameters: QueryParameterBag | undefined = undefined
    if (input.presignedUrlType) {
      queryParameters = {
        presignedUrlType: input.presignedUrlType,
      }
    }

    const asset = await this.callApiRouteWithToken<
      string,
      Asset | AssetWithPresignedUrl
    >({
      method: 'GET',
      path: `/assets/${input.assetId}`,
      queryParameters,
      metadata,
    })

    return asset
  }

  getAssets = async (
    input: GetAssetsInput,
    metadata?: DroidMapMetadata,
  ): Promise<Asset[] | AssetWithPresignedUrl[]> => {
    const assets: Asset[] | AssetWithPresignedUrl[] = []
    for (const assetId of input.assetIds) {
      const asset = await this.getAsset(
        { assetId, presignedUrlType: input.presignedUrlType },
        metadata,
      )
      if (input.presignedUrlType) {
        ;(assets as AssetWithPresignedUrl[]).push(
          asset as AssetWithPresignedUrl,
        )
      } else {
        ;(assets as Asset[]).push(asset as Asset)
      }
    }

    return assets
  }

  getAssetByPath = async (
    input: GetAssetByPathInput,
    metadata?: DroidMapMetadata,
  ): Promise<Asset | AssetWithPresignedUrl> => {
    let queryParameters: QueryParameterBag | undefined = undefined
    if (input.presignedUrlType) {
      queryParameters = {
        presignedUrlType: input.presignedUrlType,
      }
    }

    const asset = await this.callApiRouteWithToken<
      string,
      Asset | AssetWithPresignedUrl
    >({
      method: 'GET',
      path: `/assets/path/${input.assetSetId}/${input.path}`,
      queryParameters,
      metadata,
    })
    return asset
  }

  updateAsset = async (
    updateAssetInput: UpdateAssetInput,
    metadata?: DroidMapMetadata,
  ): Promise<AssetWithPresignedUrl> => {
    const { assetId } = updateAssetInput

    const asset = await this.callApiRouteWithToken<
      UpdateAssetInput,
      AssetWithPresignedUrl
    >({
      method: 'PATCH',
      path: `/assets/${assetId}`,
      body: updateAssetInput,
      metadata,
    })

    return asset
  }

  deleteAsset = async (
    input: DeleteAssetInput,
    metadata?: DroidMapMetadata,
  ): Promise<Asset> => {
    const asset = await this.callApiRouteWithToken<string, Asset>({
      method: 'DELETE',
      path: `/assets/${input.assetId}`,
      metadata,
    })

    return asset
  }

  listAssetsByAssetSet = async (
    listAssetsByAssetSetInput: ListAssetsByAssetSetInput,
    metadata?: DroidMapMetadata,
  ): Promise<Paged<AssetWithPresignedUrl>> => {
    const {
      assetSetId,
      presignedUrlType,
      next,
      prev,
      pageSize,
      category,
      assetTypes,
    } = listAssetsByAssetSetInput

    let queryParameters: QueryParameterBag =
      queryParameterBagFromPaginationControls({
        next,
        prev,
        pageSize,
      })

    queryParameters = {
      ...queryParameters,
      ...(presignedUrlType && { presignedUrlType }),
      // droidmap#2887. `assetTypes` is sent even when empty — an empty
      // category legitimately matches nothing, and dropping it would widen
      // the request to the whole set.
      ...(category && { category }),
      ...(assetTypes && { assetType: [...assetTypes] }),
    }

    const assets = await this.callApiRouteWithToken<
      ListAssetsByAssetSetInput,
      Paged<AssetWithPresignedUrl>
    >({
      method: 'GET',
      path: `/asset-sets/${assetSetId}/assets`,
      queryParameters,
      metadata,
    })

    if (!assets) {
      throw new Error('Failed to list assets')
    }

    return assets
  }

  /**
   * List a set's assets without presigned URLs (droidmap#2771).
   *
   * For callers that need per-asset coordinates or a file list but never a
   * URL — the signed shape costs two signatures an asset and ~20x the bytes.
   */
  listSlimAssetsByAssetSet = async (
    listAssetsByAssetSetInput: ListAssetsByAssetSetInput,
    metadata?: DroidMapMetadata,
  ): Promise<Paged<SlimAsset>> => {
    const { assetSetId, next, prev, pageSize, category, assetTypes } =
      listAssetsByAssetSetInput

    const queryParameters: QueryParameterBag = {
      ...queryParameterBagFromPaginationControls({ next, prev, pageSize }),
      projection: 'slim',
      ...(category && { category }),
      ...(assetTypes && { assetType: [...assetTypes] }),
    }

    const assets = await this.callApiRouteWithToken<
      ListAssetsByAssetSetInput,
      Paged<SlimAsset>
    >({
      method: 'GET',
      path: `/asset-sets/${assetSetId}/assets`,
      queryParameters,
      metadata,
    })

    if (!assets) {
      throw new Error('Failed to list assets')
    }

    return assets
  }

  /* ------------------------- Multipart Upload ------------------------- */

  initiateMultipartUpload = async (
    input: {
      assetId: string
      fileSize: number
      partSize?: number
    },
    metadata?: DroidMapMetadata,
  ): Promise<WithMultipartPresignedUrls> => {
    const result = await this.callApiRouteWithToken<
      { assetId: string; fileSize: number; partSize?: number },
      WithMultipartPresignedUrls
    >({
      method: 'POST',
      path: '/assets/multipart/initiate',
      body: input,
      metadata,
    })

    if (!result) {
      throw new Error('Failed to initiate multipart upload')
    }

    return result
  }

  completeMultipartUpload = async (
    input: {
      assetId: string
      uploadId: string
      parts: Array<{ partNumber: number; etag: string }>
    },
    metadata?: DroidMapMetadata,
  ): Promise<{ success: boolean }> => {
    const result = await this.callApiRouteWithToken<
      {
        assetId: string
        uploadId: string
        parts: Array<{ partNumber: number; etag: string }>
      },
      { success: boolean }
    >({
      method: 'POST',
      path: '/assets/multipart/complete',
      body: input,
      metadata,
    })

    if (!result) {
      throw new Error('Failed to complete multipart upload')
    }

    return result
  }

  /* ----------------------- Asset Set Operations ----------------------- */

  /**
   * Start a bulk delete / copy / move over a set's assets. Returns as soon as
   * the operation is accepted — poll {@link getAssetSetOperation} for progress.
   *
   * @example
   * // Split: move the selected images into a new set
   * const op = await assetClient.createAssetSetOperation({
   *   sourceAssetSetId,
   *   type: AssetSetOperationType.Move,
   *   assetIds: selectedIds,
   *   target: { name: 'Flight 1 - north' },
   * })
   */
  createAssetSetOperation = async (
    input: CreateAssetSetOperationInput,
    metadata?: DroidMapMetadata,
  ): Promise<AssetSetOperation> => {
    const { sourceAssetSetId, ...body } = input

    return this.callApiRouteWithToken<typeof body, AssetSetOperation>({
      method: 'POST',
      path: `/asset-sets/${sourceAssetSetId}/operations`,
      body,
      metadata,
    })
  }

  getAssetSetOperation = async (
    input: GetAssetSetOperationInput,
    metadata?: DroidMapMetadata,
  ): Promise<AssetSetOperation> => {
    return this.callApiRouteWithToken<never, AssetSetOperation>({
      method: 'GET',
      path: `/asset-set-operations/${input.operationId}`,
      metadata,
    })
  }

  /* ------------------------- Download Jobs ------------------------- */

  initiateAssetSetDownload = async (
    input: InitiateAssetSetDownloadInput,
    metadata?: DroidMapMetadata,
  ): Promise<InitiateAssetSetDownloadResponse> => {
    const result = await this.callApiRouteWithToken<
      InitiateAssetSetDownloadInput,
      InitiateAssetSetDownloadResponse
    >({
      method: 'POST',
      path: `/asset-sets/${input.assetSetId}/downloads`,
      metadata,
    })

    if (!result) {
      throw new Error('Failed to initiate asset set download')
    }

    return result
  }

  getDownloadJob = async (
    input: GetDownloadJobInput,
    metadata?: DroidMapMetadata,
  ): Promise<GetDownloadJobResponse> => {
    const result = await this.callApiRouteWithToken<
      GetDownloadJobInput,
      GetDownloadJobResponse
    >({
      method: 'GET',
      path: `/download-jobs/${input.downloadJobId}`,
      metadata,
    })

    if (!result) {
      throw new Error('Failed to get download job')
    }

    return result
  }

  /* --------------------------- Image Annotations --------------------------- */

  listImageAnnotations = async (
    input: ListImageAnnotationsInput,
    metadata?: DroidMapMetadata,
  ): Promise<Paged<ImageAnnotation>> => {
    const { assetSetId, next, pageSize } = input
    const result = await this.callApiRouteWithToken<
      never,
      Paged<ImageAnnotation>
    >({
      method: 'GET',
      path: `/asset-sets/${assetSetId}/image-annotations`,
      queryParameters: queryParameterBagFromPaginationControls({
        next,
        pageSize,
      }),
      metadata,
    })
    return result ?? { items: [] }
  }

  createImageAnnotation = async (
    input: CreateImageAnnotationInput,
    metadata?: DroidMapMetadata,
  ): Promise<ImageAnnotation> => {
    const { assetSetId, ...body } = input
    const result = await this.callApiRouteWithToken<
      Omit<CreateImageAnnotationInput, 'assetSetId'>,
      ImageAnnotation
    >({
      method: 'POST',
      path: `/asset-sets/${assetSetId}/image-annotations`,
      body,
      metadata,
    })
    if (!result) {
      throw new Error('Failed to create image annotation')
    }
    return result
  }

  updateImageAnnotation = async (
    input: UpdateImageAnnotationInput,
    metadata?: DroidMapMetadata,
  ): Promise<ImageAnnotation> => {
    const { assetSetId, annotationId, ...body } = input
    const result = await this.callApiRouteWithToken<
      Omit<UpdateImageAnnotationInput, 'assetSetId' | 'annotationId'>,
      ImageAnnotation
    >({
      method: 'PATCH',
      path: `/asset-sets/${assetSetId}/image-annotations/${annotationId}`,
      body,
      metadata,
    })
    if (!result) {
      throw new Error('Failed to update image annotation')
    }
    return result
  }

  deleteImageAnnotation = async (
    input: DeleteImageAnnotationInput,
    metadata?: DroidMapMetadata,
  ): Promise<void> => {
    await this.callApiRouteWithToken<never, unknown>({
      method: 'DELETE',
      path: `/asset-sets/${input.assetSetId}/image-annotations/${input.annotationId}`,
      metadata,
    })
  }

  listNoObjectMarks = async (
    input: ListNoObjectMarksInput,
    metadata?: DroidMapMetadata,
  ): Promise<NoObjectMarks> => {
    const result = await this.callApiRouteWithToken<never, NoObjectMarks>({
      method: 'GET',
      path: `/asset-sets/${input.assetSetId}/no-object-marks`,
      metadata,
    })
    return result ?? { assetIds: [] }
  }

  setNoObjectMark = async (
    input: SetNoObjectMarkInput,
    metadata?: DroidMapMetadata,
  ): Promise<void> => {
    await this.callApiRouteWithToken<never, unknown>({
      method: 'PUT',
      path: `/asset-sets/${input.assetSetId}/no-object-marks/${input.assetId}`,
      metadata,
    })
  }

  clearNoObjectMark = async (
    input: SetNoObjectMarkInput,
    metadata?: DroidMapMetadata,
  ): Promise<void> => {
    await this.callApiRouteWithToken<never, unknown>({
      method: 'DELETE',
      path: `/asset-sets/${input.assetSetId}/no-object-marks/${input.assetId}`,
      metadata,
    })
  }

  /* ------------------------------ Share Links ------------------------------ */

  createShareLink = async (
    input: CreateShareLinkInput,
    metadata?: DroidMapMetadata,
  ): Promise<CreateShareLinkResponse> => {
    const result = await this.callApiRouteWithToken<
      Pick<CreateShareLinkInput, 'expiresInHours'>,
      CreateShareLinkResponse
    >({
      method: 'POST',
      path: `/asset-sets/${input.assetSetId}/share-links`,
      body: { expiresInHours: input.expiresInHours },
      metadata,
    })

    if (!result) {
      throw new Error('Failed to create share link')
    }

    return result
  }

  listShareLinks = async (
    input: ListShareLinksInput,
    metadata?: DroidMapMetadata,
  ): Promise<ListShareLinksResponse> => {
    const queryParameters: QueryParameterBag = {}
    if (input.page !== undefined) queryParameters.page = String(input.page)
    if (input.pageSize !== undefined)
      queryParameters.pageSize = String(input.pageSize)

    const result = await this.callApiRouteWithToken<
      never,
      ListShareLinksResponse
    >({
      method: 'GET',
      path: `/asset-sets/${input.assetSetId}/share-links`,
      queryParameters,
      metadata,
    })

    if (!result) {
      throw new Error('Failed to list share links')
    }

    return result
  }

  revokeShareLink = async (
    input: RevokeShareLinkInput,
    metadata?: DroidMapMetadata,
  ): Promise<RevokeShareLinkResponse> => {
    const result = await this.callApiRouteWithToken<
      never,
      RevokeShareLinkResponse
    >({
      method: 'DELETE',
      path: `/asset-sets/${input.assetSetId}/share-links/${input.shareLinkId}`,
      metadata,
    })

    if (!result) {
      throw new Error('Failed to revoke share link')
    }

    return result
  }

  /* --------------------------- Public share routes --------------------------
   * These hit the unauthenticated /share mount — the link token in the path
   * is the credential, so the client's bearer token may be empty.
   */

  getShareInfo = async (
    input: GetShareInfoInput,
    metadata?: DroidMapMetadata,
  ): Promise<GetShareInfoResponse> => {
    const result = await this.callApiRouteWithToken<
      never,
      GetShareInfoResponse
    >({
      method: 'GET',
      path: `/share/${input.token}`,
      metadata,
    })

    if (!result) {
      throw new Error('Failed to get share info')
    }

    return result
  }

  initiateShareDownload = async (
    input: InitiateShareDownloadInput,
    metadata?: DroidMapMetadata,
  ): Promise<InitiateShareDownloadResponse> => {
    const result = await this.callApiRouteWithToken<
      never,
      InitiateShareDownloadResponse
    >({
      method: 'POST',
      path: `/share/${input.token}/downloads`,
      metadata,
    })

    if (!result) {
      throw new Error('Failed to initiate share download')
    }

    return result
  }

  getShareDownloadJob = async (
    input: GetShareDownloadInput,
    metadata?: DroidMapMetadata,
  ): Promise<GetShareDownloadResponse> => {
    const result = await this.callApiRouteWithToken<
      never,
      GetShareDownloadResponse
    >({
      method: 'GET',
      path: `/share/${input.token}/downloads/${input.downloadJobId}`,
      metadata,
    })

    if (!result) {
      throw new Error('Failed to get share download job')
    }

    return result
  }
}
